import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import Box from '@mui/material/Box';
import { colorTokens } from '../theme/tokens';
/**
 * Standardized filter pill button — pixel-perfect match to PondLogs V3-UX.
 *
 * Design spec (from PondLogs-V3-UX/src/app/pages/HelpPage.tsx):
 *   borderRadius : 12px    (rounded-xl)
 *   height       : ~28px   (py-1.5 = 6px × 2 + 16px text)
 *   padding      : 6px 12px
 *   font-size    : 12px    (text-xs)
 *   font-weight  : 600     (font-semibold)
 *
 *   Active  : background #003C71 (primary), color white, NO border
 *   Inactive: background #F4F6F8 (gray50) or white, color #646363 (gray600),
 *             border 1px solid #e8edf2 (borderLight)
 *
 * Accepts any ReactNode as children — text, icons, badge spans are all valid.
 * Can be used in any domain package — exported from @eruvaka/web-pondlogs-base.
 *
 * @example
 * // Text-only (Abbreviations category pill):
 * <AppFilterPill isActive={cat === selected} onClick={() => setSelected(cat)}>
 *   {cat}
 * </AppFilterPill>
 *
 * @example
 * // With icon + unread badge (Release Notes platform pill):
 * <AppFilterPill isActive={platform === 'WEB'} onClick={...} inactiveBg="white">
 *   <MonitorIcon sx={{ fontSize: 11, color: 'inherit' }} />
 *   Web
 *   {unread > 0 && <UnreadBadge count={unread} />}
 * </AppFilterPill>
 */
export const AppFilterPill = (props) => {
    const { isActive, onClick, children, inactiveBg = 'filled' } = props;
    const bgInactive = inactiveBg === 'white' ? colorTokens.white : colorTokens.gray50;
    return (_jsx(Box, { component: "button", onClick: onClick, sx: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: '12px',
            py: '6px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '16px',
            transition: 'background-color 0.15s',
            // Active: solid navy fill, no border
            ...(isActive
                ? {
                    bgcolor: colorTokens.primary,
                    color: colorTokens.white,
                    border: 'none',
                    // Use outline for active so box model stays identical to inactive
                    outline: 'none',
                }
                : {
                    bgcolor: bgInactive,
                    color: colorTokens.gray600,
                    border: `1px solid ${colorTokens.borderLight}`,
                    outline: 'none',
                }),
            '&:hover': {
                bgcolor: isActive ? colorTokens.primaryDark : colorTokens.gray100,
            },
            '&:focus-visible': {
                outline: `2px solid ${colorTokens.primary}`,
                outlineOffset: 2,
            },
        }, children: children }));
};
